import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AlertTriangle } from "lucide-react";
import { ApiError } from "../../../models/ApiResponse";
import InvoiceBatch from "../../../models/InvoiceBatch";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import Modal from "../../Core/Modal/Modal";
import Flex from "../../Layout/Flex";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import InvoiceExportStatusChip from "../InvoiceExportStatus/InvoiceExportStatusChip";
import ButtonNew from "../../Core/Button/ButtonNew";
import {
	countOpenExportIssues,
	describeExportSummary,
	getInvoiceExportIssues,
	hasOpenExportIssues,
	invoiceCustomerName,
} from "../../../utils/invoiceExport.utils";

interface Props {
	invoiceBatch?: InvoiceBatch;
	error?: ApiError;
	onClose?: () => void;
	/**
	 * Leave the result for the batch itself, where per-invoice export
	 * follow-ups are handled. Offered when the export left any.
	 */
	onShowInvoiceBatch?: () => void;
	/**
	 * Progress of a dry run ("Testkör") instead of a real run: waits for
	 * `dryRun.status` to leave RUNNING. The batch itself stays PREVIEW and the
	 * result is shown on the batch page, so the modal only reports the outcome.
	 */
	dryRun?: boolean;
}

/**
 * Modal shown when invoice batch is processing.
 *
 * Will show in different states depending on if invoice batch is
 * processing, processed or failed.
 */
const InvoiceBatchProcessingModal: React.FC<Props> = ({ dryRun, ...props }) =>
	dryRun ? <DryRunProgress {...props} /> : <ProcessingProgress {...props} />;

const ProcessingProgress: React.FC<Omit<Props, "dryRun">> = ({
	invoiceBatch,
	error,
	onClose,
	onShowInvoiceBatch,
}) => {
	const { t } = useTranslation();

	const [showLog, setShowLog] = useState(false);
	const [showCreatedCustomers, setShowCreatedCustomers] = useState(false);
	const [showUpdatedCustomers, setShowUpdatedCustomers] = useState(false);
	const [showProcessingWarnings, setShowProcessingWarnings] = useState(false);

	function getErrorDescription() {
		if (error) {
			return `${error.detail} (id ${error.id})`;
		}
		return "";
	}

	function showDiff(update: any, prev: any) {
		const updates = Object.keys(update).map(
			(k) => k + ": " + JSON.stringify(prev[k]) + " -> " + JSON.stringify(update[k])
		);
		window.alert(updates.join("\n"));
	}

	const processingLog = invoiceBatch?.processingResult?.log || [];
	const processingWarnings = invoiceBatch?.processingResult?.processingWarnings || [];
	const createdCustomers = invoiceBatch?.processingResult?.createdCustomers || [];
	const updatedCustomers = invoiceBatch?.processingResult?.updatedCustomers || [];

	// Invoices the export did not handle cleanly (Visma.net): failed, to be
	// checked in Visma, or created without their specification
	const affectedInvoices = useMemo(
		() =>
			(invoiceBatch?.invoices ?? [])
				.map(({ invoice }) => ({ invoice, issues: getInvoiceExportIssues(invoice) }))
				.filter(({ issues }) => issues.length > 0),
		[invoiceBatch]
	);

	// Counted from the invoices when the batch was loaded with them, else from
	// the batch's own summary
	const openCounts = invoiceBatch?.invoices?.length
		? countOpenExportIssues(invoiceBatch.invoices.map(({ invoice }) => invoice))
		: invoiceBatch?.exportSummary;

	const isPartial =
		invoiceBatch?.status === "PROCESSED" &&
		(hasOpenExportIssues(openCounts) || affectedInvoices.length > 0);

	const showCloseBtn =
		error || ["PROCESSED", "FAILED", "ROLLED_BACK"].includes(invoiceBatch?.status as string);
	const showLoading = !error && (!invoiceBatch || invoiceBatch.status === "PROCESSING");

	return (
		<Modal maxWidth="650px" centerContent={false} onClose={onClose}>
			<Styles>
				{showLoading && (
					<>
						<LoadingIndicator showText={t("invoiceBatch.processingModal.loading") as string} />
						<AppText textAlign="center" margin="1rem 0">
							{t("invoiceBatch.processingModal.loadingInfo")}
						</AppText>
					</>
				)}

				{(error || invoiceBatch?.status === "FAILED") && (
					<>
						<AppText textAlign="center" size="l" margin="1rem 0" color="danger" weight="medium">
							{t("invoiceBatch.processingModal.failure")}
						</AppText>
						<AppText textAlign="center" margin="1rem 0" color="danger" size="xs">
							{getErrorDescription()}
						</AppText>
					</>
				)}

				{isPartial && (
					<div className="flex flex-col items-center">
						<AlertTriangle size={44} className="text-amber-500" />
						<AppText textAlign="center" margin="1rem 0 0.25rem 0" weight="medium" size="l">
							{t("invoiceBatch.processingModal.partial")}
						</AppText>
						{hasOpenExportIssues(openCounts) && (
							<p className="m-0 text-center text-sm text-gray-600">
								{describeExportSummary(openCounts, t as any)}
							</p>
						)}
					</div>
				)}

				{affectedInvoices.length > 0 && (
					<div className="mt-4">
						<p className="mb-2 text-sm text-gray-700">
							{t("invoiceBatch.processingModal.partialInfo")}
						</p>
						<ul className="m-0 max-h-[35vh] list-none overflow-auto rounded-md border border-solid border-gray-200 p-0">
							{affectedInvoices.map(({ invoice, issues }) => (
								<li
									key={invoice.id}
									className="flex flex-col gap-1 border-0 border-b border-solid border-gray-100 px-3 py-2 last:border-b-0"
								>
									<span className="text-sm font-medium text-gray-900">
										{invoiceCustomerName(invoice)}
									</span>
									{issues.map((issue) => (
										<div key={issue.kind} className="flex flex-wrap items-start gap-2">
											<InvoiceExportStatusChip issue={issue} />
											<span className="whitespace-normal break-words text-xs text-gray-600">
												{issue.reason}
											</span>
										</div>
									))}
								</li>
							))}
						</ul>
					</div>
				)}

				{invoiceBatch?.status === "PROCESSED" && !isPartial && (
					<Flex alignItems="center" direction="column">
						<AppIcon icon="check" size="48px" />
						<AppText textAlign="center" margin="1rem 0" weight="medium" size="l">
							{t("invoiceBatch.processingModal.processed")}
						</AppText>
					</Flex>
				)}

				{createdCustomers.length > 0 && (
					<AppText
						margin="1rem 0 0.5rem 0"
						cursor="pointer"
						onClick={() => setShowCreatedCustomers(!showCreatedCustomers)}
					>
						{t("invoiceBatch.processingModal.createdCustomers", {
							count: createdCustomers.length,
						})}
					</AppText>
				)}

				{showCreatedCustomers && (
					<div className="log">
						{createdCustomers.map((row) => (
							<AppText margin="0.4rem 0" size="s" color="grayTextDark" key={row.id}>
								{row.name} ({row.customerNumber})
							</AppText>
						))}
					</div>
				)}

				{updatedCustomers.length > 0 && (
					<AppText
						margin="1rem 0 0.5rem 0"
						cursor="pointer"
						onClick={() => setShowUpdatedCustomers(!showUpdatedCustomers)}
					>
						{t("invoiceBatch.processingModal.updatedCustomers", {
							count: updatedCustomers.length,
						})}
					</AppText>
				)}

				{showUpdatedCustomers && (
					<div className="log">
						{updatedCustomers.map((row) => (
							<AppText
								margin="0.5rem 0"
								size="s"
								color="grayTextDark"
								key={row.id}
								onClick={() => showDiff(row.update, row.prev)}
							>
								{row.name} ({row.customerNumber})
							</AppText>
						))}
					</div>
				)}

				{processingWarnings.length > 0 && (
					<AppText
						margin="1rem 0 0.5rem 0"
						cursor="pointer"
						onClick={() => setShowProcessingWarnings(!showProcessingWarnings)}
					>
						{t("invoiceBatch.processingModal.processingWarnings", {
							count: processingWarnings.length,
						})}
					</AppText>
				)}

				{showProcessingWarnings && (
					<div className="log">
						{processingWarnings.map((row) => (
							<AppText margin="0.4rem 0" size="s" color="grayTextDark" key={row}>
								{row}
							</AppText>
						))}
					</div>
				)}

				{processingLog.length > 0 && (
					<AppText margin="1rem 0" cursor="pointer" onClick={() => setShowLog(!showLog)}>
						{t("invoiceBatch.processingModal.log", { count: processingLog.length })}
					</AppText>
				)}

				{showLog && (
					<div className="log">
						{processingLog.map((row, i) => (
							<AppText margin="0.4rem 0" size="s" color="grayTextDark" key={i}>
								{row}
							</AppText>
						))}
					</div>
				)}

				{showCloseBtn && isPartial && (
					<div className="mt-4 flex justify-center gap-3">
						{onShowInvoiceBatch && (
							<ButtonNew variant="primary" onClick={onShowInvoiceBatch}>
								{t("invoiceBatch.processingModal.showBatch")}
							</ButtonNew>
						)}
						<ButtonNew variant="neutral" className="border-solid" onClick={onClose}>
							{t("CLOSE")}
						</ButtonNew>
					</div>
				)}

				{showCloseBtn && !isPartial && (
					<Flex justifyContent="center">
						<Button width="130px" margin="1rem 0 0 0" onClick={onClose}>
							{t("CLOSE")}
						</Button>
					</Flex>
				)}
			</Styles>
		</Modal>
	);
};

/** Progress and outcome of a dry run, with the processing modal's look. */
const DryRunProgress: React.FC<Omit<Props, "dryRun">> = ({ invoiceBatch, error, onClose }) => {
	const { t } = useTranslation();

	const status = invoiceBatch?.dryRun?.status;
	const isRunning = !error && (!invoiceBatch || !status || status === "RUNNING");
	const isFailed = !!error || status === "ERROR";
	const failureDetail = error
		? `${error.detail ?? error.title ?? ""}${error.id ? ` (id ${error.id})` : ""}`
		: invoiceBatch?.dryRun?.errorReason;

	return (
		<Modal maxWidth="650px" centerContent={false} onClose={onClose}>
			{isRunning && (
				<>
					<LoadingIndicator showText={t("invoiceBatch.dryRun.processingModal.loading") as string} />
					<AppText textAlign="center" margin="1rem 0">
						{t("invoiceBatch.dryRun.processingModal.loadingInfo")}
					</AppText>
				</>
			)}

			{isFailed && (
				<>
					<AppText textAlign="center" size="l" margin="1rem 0" color="danger" weight="medium">
						{t("invoiceBatch.dryRun.processingModal.failure")}
					</AppText>
					{failureDetail && (
						<p className="m-0 mb-4 whitespace-pre-wrap break-words text-center text-sm text-gray-700">
							{failureDetail}
						</p>
					)}
				</>
			)}

			{!isRunning && !isFailed && (
				<Flex alignItems="center" direction="column">
					<AppIcon icon="check" size="48px" />
					<AppText textAlign="center" margin="1rem 0 0.25rem 0" weight="medium" size="l">
						{t("invoiceBatch.dryRun.processingModal.done")}
					</AppText>
					<p className="m-0 mb-2 text-center text-sm text-gray-600">
						{t("invoiceBatch.dryRun.processingModal.doneInfo")}
					</p>
				</Flex>
			)}

			{!isRunning && (
				<Flex justifyContent="center">
					<Button width="130px" margin="1rem 0 0 0" onClick={onClose}>
						{t("CLOSE")}
					</Button>
				</Flex>
			)}
		</Modal>
	);
};

export default InvoiceBatchProcessingModal;

const Styles = styled.div`
	.log {
		max-height: 35vh;
		overflow: auto;
		white-space: nowrap;
	}
`;
